<script>
/**
 * @component ScheduleView
 * @description Vista de atenciones del día para el doctor.
 * Muestra los turnos del día con navegación por fecha,
 * estadísticas rápidas y acciones por paciente.
 */
import Spinner from "@/components/ui/Spinner";
import BaseSubNav from "@/components/ui/BaseSubNav";
import pagination from "@/mixins/pagination";
import slotService from "@/services/slot";
import helperService from "@/services/helper";
import ModalAppointment from "@/components/appointment/ModalAppointment";
import moment from "moment";
import {
  formatTimeAR,
  formatDateAR,
  startOfDayAR,
  endOfDayAR,
  toApiUTC,
} from "@/services/datetime";
import { getPatientAdmissionStatus } from "@/services/constants";
import { isVirtualAttention } from "@/services/attention-type";
import { formatScheduleCoverage } from "@/services/coverage";
import { getAppointmentStatus } from "@/services/appointment-status";
import observationService, {
  indexByAppointment,
  appointmentIdsOf,
} from "@/services/appointment-observation";
import { mapGetters } from "vuex";
import parametrizationService from "@/services/parametrization";
import appointmentService from "@/services/appointment";
import AIPatientSummaryModal from "@/components/ai/AIPatientSummaryModal.vue";

export default {
  name: "ScheduleView",

  mixins: [pagination],

  components: {
    Spinner,
    BaseSubNav,
    ModalAppointment,
    AIPatientSummaryModal,
  },

  data() {
    return {
      isLoading: false,
      slots: [],
      /** @description UC-REG-275 — Observaciones por ID de turno. */
      observations: {},
      filterByOccupied: true,
      selectedSlot: undefined,
      isModalVisible: false,
      selectedDate: new Date(),
      isCallProviderEnabled: false,
      isDirectAdmissionEnabled: false,
      loadingCalls: {},
      aiSummaryVisible: false,
      aiSummaryPatientId: null,
      aiSummaryPatientName: "",
    };
  },

  computed: {
    ...mapGetters(["isDoctor"]),

    /**
     * @description UC-REG-079. Filtra slots cuya fecha (en TZ del tenant)
     *              coincide con el día seleccionado. Comparar via
     *              formatDateAR garantiza que el día sea el del calendario
     *              del tenant — antes el comparison usaba startOf("day")
     *              del moment con TZ del browser, que rompía si el dev/PM
     *              abría desde otro huso.
     * @returns {Array}
     */
    filteredSlots() {
      const selectedDayKey = formatDateAR(this.selectedDate);
      return this.slots.filter((slot) => {
        return formatDateAR(slot.startsAt) === selectedDayKey;
      });
    },

    /** @returns {string} Fecha formateada con indicador "Hoy" */
    formattedDate() {
      const selectedDate = moment(this.selectedDate);
      const today = moment();
      if (selectedDate.isSame(today, "day")) {
        return `Hoy ${selectedDate.format("DD/MM")}`;
      }
      return selectedDate
        .format("dddd DD/MM")
        .replace(/^\w/, (c) => c.toUpperCase());
    },

    /** @returns {boolean} Si la fecha seleccionada es hoy */
    isToday() {
      return moment(this.selectedDate).isSame(moment(), "day");
    },

    /**
     * @description Stats calculadas localmente desde filteredSlots
     * @returns {{ total: number, pending: number, admitted: number, done: number }}
     */
    statsCards() {
      const slots = this.filteredSlots || [];
      const withAppointment = slots.filter((s) => s.appointment);
      return {
        total: withAppointment.length,
        pending: withAppointment.filter(
          (s) => s.appointment?.status === "pending"
        ).length,
        admitted: withAppointment.filter(
          (s) => s.appointment?.status === "admitted"
        ).length,
        done: withAppointment.filter(
          (s) =>
            s.appointment?.status === "done" ||
            s.appointment?.status === "started"
        ).length,
      };
    },
  },

  watch: {
    filterByOccupied() {
      this.getTodaySlots();
    },
    selectedDate() {
      this.getTodaySlots();
    },
  },

  async created() {
    this.getTodaySlots();
    await Promise.all([
      this.checkCallProviderEnabled(),
      this.checkDirectAdmissionEnabled(),
    ]);
  },

  /**
   * @description Arranca el auto-refresh de la lista: cada 15s re-fetchea en
   * silencio, así el médico ve en vivo las admisiones presenciales y los
   * pacientes que entran a la videoconsulta, sin recargar la pantalla. Solo
   * corre si se está mirando el día de hoy y la pestaña está visible.
   */
  mounted() {
    this.presenceTimer = setInterval(() => {
      const isToday = moment(this.selectedDate).isSame(moment(), "day");
      if (isToday && !document.hidden) this.getTodaySlots(true);
    }, 15000);
  },

  beforeDestroy() {
    if (this.presenceTimer) clearInterval(this.presenceTimer);
  },

  methods: {
    onPageChange() {
      this.getTodaySlots();
    },

    /** @param {number} days - Días a avanzar/retroceder */
    changeDate(days) {
      const newDate = new Date(this.selectedDate);
      newDate.setDate(newDate.getDate() + days);
      this.selectedDate = newDate;
    },

    /** Ir a hoy */
    goToToday() {
      this.selectedDate = new Date();
    },

    /**
     * Carga los slots del día seleccionado.
     * @param {boolean} silent - Si es true no muestra el loader: lo usa el
     *   auto-refresh en segundo plano para no parpadear la pantalla.
     */
    async getTodaySlots(silent = false) {
      if (!silent) this.isLoading = true;
      // UC-REG-079: boundaries del dia en TZ del tenant (no del browser).
      // Antes habia bug si el usuario abria desde un huso distinto.
      const startOfDay = toApiUTC(startOfDayAR(this.selectedDate));
      const endOfDay = toApiUTC(endOfDayAR(this.selectedDate));

      const slots = await slotService.getSlotsByDoctor({
        doctorId: this.doctorId,
        params: {
          startsAt: { $gte: startOfDay },
          endsAt: { $lte: endOfDay },
          ...(this.filterByOccupied && { appointment: { $exists: true } }),
          populate: [
            {
              path: "appointment",
              populate: "patient",
            },
            { path: "medicalSpecialties institution doctor" },
          ],
          offset: this.pagination.offset,
          limit: this.pagination.limit,
        },
      });

      this.slots = slots.docs;
      this.pagination.total = slots.total || 0;
      if (!silent) this.isLoading = false;
      this.observations = indexByAppointment(
        await observationService.getByAppointments(appointmentIdsOf(this.slots))
      );
    },

    /**
     * @description UC-REG-275 — Texto de la observación del turno del slot.
     * @param {Object} slot
     * @returns {string}
     */
    slotObservationText(slot) {
      const id = slot.appointment && (slot.appointment._id || slot.appointment);
      const observation = id && this.observations[id];
      return (observation && observation.text) || "";
    },

    /** @returns {string} Tipo de atención formateado */
    getAttentionType(slot) {
      const labels = {
        virtual: "Virtual",
        "in-person": "Presencial",
      };
      if (slot.appointment?.attentionType) {
        return labels[slot.appointment.attentionType] || "Indefinido";
      }
      if (Array.isArray(slot.attentionType) && slot.attentionType.length > 0) {
        return slot.attentionType
          .map((t) => labels[t] || "Indefinido")
          .join(", ");
      }
      return "Indefinido";
    },

    /** @returns {boolean} Si el turno es virtual */
    isVirtual(slot) {
      return isVirtualAttention(slot);
    },

    /**
     * @description UC-REG-273: estado a mostrar en la fila. "En sala" no se
     *              repite: ya lo muestra la etiqueta en vivo del paciente.
     * @param {Object} slot
     * @returns {{key: string, label: string, className: string}|null}
     */
    rowStatus(slot) {
      const status = getAppointmentStatus(
        slot.appointment && {
          ...slot.appointment,
          endsAt: slot.appointment.endsAt || slot.endsAt,
        }
      );
      return status && status.key !== "admitted" ? status : null;
    },

    handleSlot(slot) {
      if (slot.appointment) {
        this.$router.push({
          name: "appointment",
          params: { appointmentId: slot.appointment._id },
        });
      } else if (slot.available) {
        this.showModal(slot);
      }
    },

    showModal(slot) {
      this.isModalVisible = true;
      this.selectedSlot = slot;
    },

    closeModal() {
      this.isModalVisible = false;
    },

    getFullName(user) {
      return helperService.getFullName(user);
    },

    doctorId() {
      return this.$store.state.user._id;
    },

    /**
     * @description Abre el modal de resumen IA para un paciente
     * @param {Object} patient - Objeto paciente del slot
     */
    openAISummary(patient) {
      this.aiSummaryPatientId = patient._id;
      this.aiSummaryPatientName = `${patient.firstName} ${patient.lastName}`;
      this.aiSummaryVisible = true;
    },

    getDniInfo(patient) {
      return patient?.governmentId?.number
        ? `${patient.governmentId.type.toUpperCase()} ${
            patient.governmentId.number
          }`
        : "";
    },

    getHealthInsurance(patient) {
      return formatScheduleCoverage(patient);
    },

    /**
     * @description Formatea la hora del slot en TZ del tenant (UC-REG-079).
     * @param {Object} slot
     * @returns {string} HH:mm o "N/A"
     */
    getAppointmentHour(slot) {
      return slot && slot.startsAt ? formatTimeAR(slot.startsAt) : "N/A";
    },

    goToPatientDashboard(id) {
      helperService.callIfNoSelectedText(() =>
        this.$router.push({
          name: "patient-dashboard",
          params: { id },
        })
      );
    },

    async handleSlotStatus(slot) {
      if (
        slot?.appointment?.status !== "pending" &&
        slot?.appointment?.status !== "started" &&
        slot?.appointment?.status !== "admitted"
      ) {
        this.goToPatientDashboard(slot?.appointment?.patient?._id);
        return;
      }
      if (
        this.isDirectAdmissionEnabled &&
        slot?.appointment?.status === "pending"
      ) {
        await this.handleDirectAdmission(slot);
        return;
      }
      this.handleSlot(slot);
      this.$message.success("Turno iniciado con éxito");
    },

    async handleDirectAdmission(slot) {
      if (!slot?.appointment?._id) return;
      try {
        const appointment = await appointmentService.admitAppointment(
          slot.appointment._id
        );
        const slotIndex = this.slots.findIndex((s) => s._id === slot._id);
        if (slotIndex !== -1 && this.slots[slotIndex].appointment) {
          this.$set(
            this.slots[slotIndex].appointment,
            "status",
            appointment.status
          );
        }
        this.$message.success("Turno admitido con éxito");
      } catch (error) {
        // Error handling is done by the service
      }
    },

    isSlotClosed(slot) {
      return (
        slot?.appointment?.status !== "pending" &&
        slot?.appointment?.status !== "started" &&
        slot?.appointment?.status !== "admitted"
      );
    },

    getAppointmentStatus(slot) {
      return slot?.appointment?.status || "pending";
    },

    getStatusInfo(slot) {
      const status = this.getAppointmentStatus(slot);
      return getPatientAdmissionStatus(status);
    },

    /** @returns {string} Clase CSS del badge de estado */
    getStatusBadgeClass(slot) {
      const status = this.getAppointmentStatus(slot);
      const map = {
        pending: "badge--pending",
        admitted: "badge--admitted",
        started: "badge--in-progress",
        done: "badge--done",
      };
      return map[status] || "badge--pending";
    },

    getButtonText(slot) {
      if (!slot?.appointment) return "Atender";
      return this.getStatusInfo(slot).label;
    },

    isAppointmentAdmitted(slot) {
      return slot?.appointment?.status === "admitted";
    },

    /**
     * @description Si el paciente está conectado ahora a la sala de espera de
     * la videoconsulta. Se basa en el heartbeat `patientLastSeenAt` que el
     * backend refresca con cada poll de la landing del paciente (~8s).
     * @param {Object} slot
     * @returns {boolean}
     */
    isPatientInRoom(slot) {
      const seen = slot?.appointment?.patientLastSeenAt;
      if (!seen) return false;
      return Date.now() - new Date(seen).getTime() < 30000;
    },

    /**
     * @description Indicador unificado "paciente en sala": admitido en persona
     * por secretaría O conectado a la videoconsulta. Gobierna el color verde
     * del botón y el badge "En sala".
     * @param {Object} slot
     * @returns {boolean}
     */
    isInRoom(slot) {
      return this.isAppointmentAdmitted(slot) || this.isPatientInRoom(slot);
    },

    isCallButtonLoading(slot) {
      if (!slot?.appointment?._id) return false;
      return this.loadingCalls[slot.appointment._id] || false;
    },

    async checkCallProviderEnabled() {
      this.isCallProviderEnabled =
        await parametrizationService.isBooleanEnabled("CALL_PROVIDER_ENABLED");
    },

    async checkDirectAdmissionEnabled() {
      this.isDirectAdmissionEnabled =
        await parametrizationService.isBooleanEnabled(
          "DIRECT_ADMISSION_ENABLED"
        );
    },

    async handleCallPatient(slot) {
      if (!slot?.appointment?._id) return;
      const appointmentId = slot.appointment._id;
      this.$set(this.loadingCalls, appointmentId, true);
      try {
        await appointmentService.callPatient(appointmentId);
        this.$message.success("Llamada enviada exitosamente");
      } catch (error) {
        // Error handling is done by the service
      } finally {
        this.$set(this.loadingCalls, appointmentId, false);
      }
    },
  },
};
</script>

<template lang="pug">
  .schedule-page
    //- Stats rápidas + Hoy button
    .schedule-top-actions(v-if="!isToday")
      button.btn-today(@click="goToToday") Hoy

    //- Stats rápidas
    .schedule-stats
      .stat-card
        .stat-card__value {{ statsCards.total }}
        .stat-card__label TURNOS DEL DÍA
      .stat-card.stat-card--pending
        .stat-card__value {{ statsCards.pending }}
        .stat-card__label PENDIENTES
      .stat-card.stat-card--admitted
        .stat-card__value {{ statsCards.admitted }}
        .stat-card__label ADMITIDOS
      .stat-card.stat-card--done
        .stat-card__value {{ statsCards.done }}
        .stat-card__label ATENDIDOS

    //- Navegación de fecha
    .date-nav
      button.date-nav__arrow(@click="changeDate(-1)")
        i.el-icon-arrow-left
      .date-nav__label {{ formattedDate }}
      button.date-nav__arrow(@click="changeDate(1)")
        i.el-icon-arrow-right

    //- Loading
    .schedule-loading(v-if="isLoading")
      spinner

    //- Lista vacía
    .schedule-empty(v-if="!isLoading && !filteredSlots.length")
      .schedule-empty__icon
        i.el-icon-date
      p No hay atenciones para este día

    //- Lista de atenciones
    .schedule-list(v-if="!isLoading && filteredSlots.length")
      .attention-card(
        v-for="slot in filteredSlots"
        :key="slot._id"
        :class="{ 'attention-card--closed': isSlotClosed(slot) }"
      )
        //- Hora
        .attention-card__time
          .time-badge
            i.el-icon-time
            span {{ getAppointmentHour(slot) }}

        //- Tipo de atención — entre la hora y el paciente, en la misma fila
        .attention-card__tags(v-if="slot.appointment && slot.appointment.patient")
          span.tag(:class="isVirtual(slot) ? 'tag--virtual' : 'tag--presencial'")
            i(:class="isVirtual(slot) ? 'el-icon-video-camera' : 'el-icon-first-aid-kit'")
            |  {{ getAttentionType(slot) }}

        //- Info del paciente
        .attention-card__body(v-if="slot.appointment && slot.appointment.patient")
          .attention-card__patient
            .patient-info
              .patient-info__header
                h3.patient-info__name {{ getFullName(slot.appointment.patient) }}
                span.patient-info__age {{ slot.appointment.patient.bornAt | age }}
                span.tag-in-room(v-if="isInRoom(slot) && !isSlotClosed(slot)")
                  i.el-icon-success
                  |  En sala
                span.appt-status(v-if="rowStatus(slot)" :class="rowStatus(slot).className") {{ rowStatus(slot).label }}
              p.patient-info__details(:title="getHealthInsurance(slot.appointment.patient)") {{ getDniInfo(slot.appointment.patient) }}
                span(v-if="getHealthInsurance(slot.appointment.patient)")  · {{ getHealthInsurance(slot.appointment.patient) }}
              //- UC-REG-275 — observación en una línea; completa en el tooltip
              p.patient-info__note(
                v-if="slotObservationText(slot)"
                :title="slotObservationText(slot)"
              )
                i.el-icon-chat-line-square
                |  {{ slotObservationText(slot) }}

        //- Cuerpo vacío (slot libre)
        .attention-card__body(v-else)
          .patient-info
            h3.patient-info__name Turno libre

        //- Acciones
        .attention-card__actions
          button.btn-action.btn-action--secondary(
            v-if="slot.appointment && slot.appointment.patient"
            @click="goToPatientDashboard(slot.appointment.patient._id)"
          )
            i.el-icon-document
            span Ver historia clínica

          button.btn-action.btn-action--ai(
            v-if="slot.appointment && slot.appointment.patient"
            @click="openAISummary(slot.appointment.patient)"
          )
            i.el-icon-magic-stick
            span Resumen IA

          button.btn-action.btn-action--secondary(
            v-if="slot.appointment && slot.appointment.patient"
            @click="goToPatientDashboard(slot.appointment.patient._id)"
          )
            i.el-icon-folder-opened
            span Ver adjuntos

          button.btn-action.btn-action--call(
            v-if="slot.appointment && isCallProviderEnabled"
            @click="handleCallPatient(slot)"
            :disabled="!isAppointmentAdmitted(slot) || isCallButtonLoading(slot)"
          )
            i.el-icon-phone-outline
            span {{ isCallButtonLoading(slot) ? 'Llamando...' : 'Llamar' }}

          button.btn-action(
            v-if="slot.appointment"
            :class="isInRoom(slot) && !isSlotClosed(slot) ? 'btn-action--ready' : 'btn-action--primary'"
            :disabled="isSlotClosed(slot)"
            @click="handleSlotStatus(slot)"
          )
            i(:class="isVirtual(slot) ? 'el-icon-video-camera' : 'el-icon-right'")
            span {{ isSlotClosed(slot) ? 'Atendido' : isAppointmentAdmitted(slot) ? 'Iniciar' : 'Admitir' }}

    //- Paginación
    pagination(
      :isLoading="isLoading"
      :limit="pagination.limit"
      :total="pagination.total"
      @pagination="setPagination"
    )

    //- Modal
    modal-appointment(
      v-if="isModalVisible"
      :show-dialog-modal="isModalVisible"
      :selected-slot="selectedSlot"
      @close="closeModal"
    )

    AIPatientSummaryModal(
      :visible="aiSummaryVisible"
      :patientId="aiSummaryPatientId || ''"
      :patientName="aiSummaryPatientName"
      :doctorId="$store.state.user._id"
      @close="aiSummaryVisible = false"
    )
</template>

<style lang="scss" scoped>
/* ============================================
   SCHEDULE PAGE — Design System
   Navy: #002D5E | Teal: #05E5BA | BG: #f0f2f7
   Font: Plus Jakarta Sans
   ============================================ */

.schedule-page {
  padding: 0;
}

/* ---------- Top actions ---------- */
.schedule-top-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.btn-today {
  background: #002d5e;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
}

/* ---------- Stats ---------- */
.schedule-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.stat-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  text-align: center;
  border-left: 3px solid #e2e8f0;

  &--pending {
    border-left-color: #f59e0b;
  }

  &--admitted {
    border-left-color: #3b82f6;
  }

  &--done {
    border-left-color: #05e5ba;
  }

  &__value {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: #002d5e;
    line-height: 1;
  }

  &__label {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 10px;
    font-weight: 600;
    color: #6b7294;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 4px;
  }
}

/* ---------- Date Navigation ---------- */
.date-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 20px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 24px;

  &__arrow {
    background: none;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #002d5e;
    font-size: 16px;
    transition: all 0.2s;

    &:hover {
      background: #f0f2f7;
      border-color: #002d5e;
    }
  }

  &__label {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: #002d5e;
    min-width: 160px;
    text-align: center;
  }
}

/* ---------- Loading & Empty ---------- */
.schedule-loading {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}

.schedule-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;

  &__icon {
    font-size: 40px;
    color: #cbd5e1;
    margin-bottom: 12px;
  }

  p {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 15px;
    color: #6b7294;
    margin: 0;
  }
}

/* ---------- Attention Card ---------- */
.schedule-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.attention-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 20px;
  transition: all 0.2s;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 45, 94, 0.06);
  }

  &--closed {
    opacity: 0.6;
  }

  /* Hora */
  &__time {
    flex-shrink: 0;
    width: 80px;
  }

  /* Body */
  &__body {
    flex: 1;
    min-width: 0;
  }

  /* Acciones */
  &__actions {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__patient {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__tags {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
}

/* Time badge */
.time-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0f2f7;
  border-radius: 8px;
  padding: 6px 10px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #002d5e;

  i {
    font-size: 14px;
    color: #6b7294;
  }
}

/* Patient info */
.patient-info {
  min-width: 0;

  &__header {
    display: flex;
    flex-wrap: wrap; // UC-REG-273: la etiqueta de estado baja de línea si no entra
    row-gap: 2px;
    align-items: baseline;
    gap: 8px;
  }

  &__name {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: #002d5e;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__age {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    color: #94a3b8;
    flex-shrink: 0;
  }

  &__details {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    color: #6b7294;
    margin: 2px 0 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // UC-REG-275 — observación del turno en una sola línea
  &__note {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    color: #475569;
    margin: 2px 0 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* Tags */
.tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  // Ancho fijo al texto más largo ("Presencial") para que el badge
  // Virtual/Presencial nunca cambie de tamaño y el nombre del paciente
  // arranque siempre en la misma X entre filas.
  min-width: 104px;

  i {
    font-size: 12px;
  }

  &--virtual {
    background: #e8fdf8;
    color: #047857;
  }

  // Azul saturado para diferenciar a simple vista del verde de Virtual.
  &--presencial {
    background: #dbeafe;
    color: #1d4ed8;
  }

  &--status {
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
}

/* "En sala" — paciente admitido en persona o conectado a la videoconsulta */
.tag-in-room {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  background: #1f9d57;
  color: #fff;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 6px;

  i {
    font-size: 12px;
  }
}

/* Status badge colors */
.badge--pending {
  background: #fef3c7;
  color: #92400e;
}

.badge--admitted {
  background: #dbeafe;
  color: #1e40af;
}

.badge--in-progress {
  background: #d1fae5;
  color: #065f46;
}

.badge--done {
  background: #e8fdf8;
  color: #047857;
}

/* Action buttons */
.btn-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  i {
    font-size: 14px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &--primary {
    background: #002d5e;
    color: #fff;
    min-width: 124px;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }
  }

  // Estado "paciente admitido": verde — indica de un vistazo que el
  // paciente ya llegó y la consulta está lista para iniciarse. El texto
  // sigue siendo "Iniciar" (la acción); el color comunica el estado.
  // min-width igual al --primary para que "Admitir"/"Iniciar"/"Atendido"
  // ocupen siempre lo mismo y los botones queden alineados entre filas.
  &--ready {
    background: #1f9d57;
    color: #fff;
    min-width: 124px;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }
  }

  &--secondary {
    background: #f0f2f7;
    color: #002d5e;
    border: 1px solid #e2e8f0;

    &:hover:not(:disabled) {
      background: #e2e8f0;
    }
  }

  &--call {
    background: #05e5ba;
    color: #002d5e;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }

    &:disabled {
      background: #e2e8f0;
      color: #94a3b8;
    }
  }
}

/* ---------- Responsive ---------- */
@media (max-width: 768px) {
  .schedule-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .attention-card {
    flex-wrap: wrap;

    &__time {
      width: auto;
    }

    &__actions {
      width: 100%;
      justify-content: flex-end;
      padding-top: 8px;
      border-top: 1px solid #f0f2f7;
    }
  }
}

.btn-action--ai {
  background: #e6f9f9;
  color: #0e7490;
  border: 1px solid #2abfbf;

  &:hover:not(:disabled) {
    background: #d0f5f5;
  }
}
</style>
